import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Plus, Trash2, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { fmtDate, fmtDateTime, inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const SIGNAL_LABELS = { like: "Liked", dislike: "Disliked", wishlist: "Wishlisted", view: "Viewed" };
const CHANNELS = ["whatsapp", "email", "sms"];

export default function Customer360() {
  const { email: rawEmail } = useParams();
  const email = decodeURIComponent(rawEmail);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [noteText, setNoteText] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [occ, setOcc] = useState({ label: "", date: "" });
  const [prefs, setPrefs] = useState({ ring_size: "", budget: "" });

  const load = () =>
    api.get(`/admin/crm/customers/${encodeURIComponent(email)}`).then((r) => {
      setData(r.data);
      setPrefs({
        ring_size: r.data.profile?.preferences?.ring_size || "",
        budget: r.data.profile?.preferences?.budget || "",
      });
    }).catch(() => setData(false));
  useEffect(() => { load(); }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

  if (data === null) return <p className="text-sm font-mono text-neutral-400">LOADING…</p>;
  if (data === false) return <p className="text-sm text-neutral-500" data-testid="crm-360-not-found">Customer not found.</p>;

  const profile = data.profile || {};
  const patch = (body) =>
    api.patch(`/admin/crm/customers/${encodeURIComponent(email)}`, body).then(load).catch((e) => toast.error(errMsg(e)));

  const toggleConsent = (ch, val) => patch({ consents: { ...(profile.consents || {}), [ch]: val } });
  const addTag = () => {
    if (!tagInput.trim()) return;
    patch({ tags: [...(profile.tags || []), tagInput.trim()] });
    setTagInput("");
  };
  const addOccasion = () => {
    if (!occ.label.trim() || !occ.date.trim()) return;
    patch({ occasions: [...(profile.occasions || []), { label: occ.label.trim(), date: occ.date.trim(), recurring: true }] });
    setOcc({ label: "", date: "" });
  };
  const savePrefs = () => patch({ preferences: { ...(profile.preferences || {}), ...prefs } });
  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/admin/crm/customers/${encodeURIComponent(email)}/notes`, { text: noteText }).catch((e) => toast.error(errMsg(e)));
    setNoteText("");
    load();
  };
  const addTask = async () => {
    if (!taskTitle.trim()) return;
    await api.post(`/admin/crm/customers/${encodeURIComponent(email)}/tasks`, { title: taskTitle }).catch((e) => toast.error(errMsg(e)));
    setTaskTitle("");
    load();
  };
  const exportData = async () => {
    const r = await api.get(`/admin/crm/customers/${encodeURIComponent(email)}/export`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `siyaara-customer-${email}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const erase = async () => {
    if (!window.confirm(`Erase all PII for ${email}? Order financials are kept anonymised (statutory). This cannot be undone.`)) return;
    await api.delete(`/admin/crm/customers/${encodeURIComponent(email)}`).catch((e) => toast.error(errMsg(e)));
    toast.success("Customer PII erased");
    navigate("/admin/crm/customers");
  };

  const sectionCls = "bg-white border border-neutral-200 p-4";
  const headCls = "text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-3";

  return (
    <div className="space-y-4" data-testid="customer-360-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin/crm/customers" className="text-xs text-neutral-500 flex items-center gap-1 hover:text-brand" data-testid="crm-360-back">
            <ArrowLeft size={12} /> Customers 360
          </Link>
          <h1 className="font-heading text-2xl font-bold tracking-tight mt-1" data-testid="crm-360-name">
            {data.user?.name || data.orders?.[0]?.customer?.name || email}
          </h1>
          <p className="text-xs text-neutral-500">
            {email} {data.user?.phone ? `· ${data.user.phone}` : ""} · {data.user ? "Registered" : "Guest"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportData} data-testid="crm-export-btn"><Download size={14} className="mr-1" /> Export (DPDP)</Button>
          <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={erase} data-testid="crm-erase-btn"><Trash2 size={14} className="mr-1" /> Erase PII</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className={sectionCls} data-testid="crm-360-ltv"><p className={headCls}>Lifetime value</p><p className="font-heading text-xl font-bold">{inr(data.ltv)}</p></div>
        <div className={sectionCls} data-testid="crm-360-orders-count"><p className={headCls}>Orders</p><p className="font-heading text-xl font-bold">{data.order_count}</p></div>
        <div className={sectionCls}><p className={headCls}>Last order</p><p className="font-heading text-xl font-bold">{data.orders?.[0] ? fmtDate(data.orders[0].created_at) : "—"}</p></div>
        <div className={sectionCls} data-testid="crm-360-taste">
          <p className={headCls}>Taste profile</p>
          <div className="flex flex-wrap gap-1">
            {[...(data.taste?.metals || []), ...(data.taste?.purities || []), ...(data.taste?.categories || [])].map((t) => (
              <span key={t} className="text-[10px] font-mono uppercase tracking-wider bg-brand-light text-brand px-1.5 py-0.5">{t}</span>
            ))}
            {![...(data.taste?.metals || []), ...(data.taste?.purities || []), ...(data.taste?.categories || [])].length && <span className="text-xs text-neutral-400">No signals yet</span>}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className={sectionCls} data-testid="crm-360-consents">
          <p className={headCls}>Channel consent</p>
          {CHANNELS.map((ch) => (
            <div key={ch} className="flex items-center justify-between py-1.5">
              <span className="text-sm capitalize">{ch}</span>
              <Switch checked={(profile.consents || {})[ch] !== false} onCheckedChange={(v) => toggleConsent(ch, v)} data-testid={`consent-${ch}`} />
            </div>
          ))}
          <p className="text-[11px] text-neutral-400 mt-2">Off = journeys skip this channel for this customer.</p>
        </div>

        <div className={sectionCls} data-testid="crm-360-tags">
          <p className={headCls}>Tags</p>
          <div className="flex flex-wrap gap-1 mb-2">
            {(profile.tags || []).map((t) => (
              <span key={t} className="text-xs bg-neutral-100 px-2 py-0.5 flex items-center gap-1">
                {t}
                <button onClick={() => patch({ tags: profile.tags.filter((x) => x !== t) })} data-testid={`tag-remove-${t}`}><X size={11} /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-1">
            <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="e.g. bridal, vip" className="h-8 text-xs" data-testid="tag-input" />
            <Button size="sm" variant="outline" onClick={addTag} data-testid="tag-add-btn"><Plus size={13} /></Button>
          </div>
        </div>

        <div className={sectionCls} data-testid="crm-360-occasions">
          <p className={headCls}>Occasion calendar</p>
          <div className="space-y-1 mb-2">
            {(profile.occasions || []).map((o, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span>{o.label} — <span className="font-mono">{o.date}</span> (yearly)</span>
                <button onClick={() => patch({ occasions: profile.occasions.filter((_, j) => j !== i) })} data-testid={`occasion-remove-${i}`}><X size={11} /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-1">
            <Input value={occ.label} onChange={(e) => setOcc({ ...occ, label: e.target.value })} placeholder="Anniversary" className="h-8 text-xs" data-testid="occasion-label-input" />
            <Input value={occ.date} onChange={(e) => setOcc({ ...occ, date: e.target.value })} placeholder="MM-DD" className="h-8 text-xs w-20" data-testid="occasion-date-input" />
            <Button size="sm" variant="outline" onClick={addOccasion} data-testid="occasion-add-btn"><Plus size={13} /></Button>
          </div>
          <div className="flex gap-1 mt-3">
            <Input value={prefs.ring_size} onChange={(e) => setPrefs({ ...prefs, ring_size: e.target.value })} placeholder="Ring size" className="h-8 text-xs" data-testid="pref-ring-size" />
            <Input value={prefs.budget} onChange={(e) => setPrefs({ ...prefs, budget: e.target.value })} placeholder="Budget ₹" className="h-8 text-xs" data-testid="pref-budget" />
            <Button size="sm" variant="outline" onClick={savePrefs} data-testid="pref-save-btn">Save</Button>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className={sectionCls} data-testid="crm-360-orders">
          <p className={headCls}>Orders</p>
          <div className="divide-y divide-neutral-100">
            {data.orders.map((o) => (
              <div key={o.id} className="py-2 text-xs flex justify-between" data-testid={`crm-order-${o.order_number}`}>
                <span className="font-mono">{o.order_number}</span>
                <span>{inr(o.total)}</span>
                <span className="text-neutral-400">{fmtDate(o.created_at)}</span>
              </div>
            ))}
            {!data.orders.length && <p className="text-xs text-neutral-400">No orders yet.</p>}
          </div>
        </div>

        <div className={sectionCls} data-testid="crm-360-affinity">
          <p className={headCls}>Affinity (likes / dislikes / wishlist)</p>
          <div className="divide-y divide-neutral-100 max-h-64 overflow-y-auto">
            {data.affinity.filter((a) => a.signal !== "view").map((a) => (
              <div key={a.id} className="py-1.5 text-xs flex justify-between" data-testid={`crm-affinity-${a.id}`}>
                <span>{SIGNAL_LABELS[a.signal] || a.signal} — {a.title}</span>
                <span className="text-neutral-400">{a.metal} {a.purity}</span>
              </div>
            ))}
            {!data.affinity.filter((a) => a.signal !== "view").length && <p className="text-xs text-neutral-400">No likes/dislikes yet.</p>}
          </div>
        </div>

        <div className={sectionCls} data-testid="crm-360-messages">
          <p className={headCls}>Journey messages</p>
          <div className="divide-y divide-neutral-100 max-h-64 overflow-y-auto">
            {data.sends.map((s) => (
              <div key={s.id} className="py-2 text-xs" data-testid={`crm-send-${s.id}`}>
                <div className="flex justify-between"><span className="font-mono uppercase">{s.channel}</span><span className="text-neutral-400">{s.status}</span></div>
                <p className="text-neutral-500 mt-0.5">{s.body}</p>
                <p className="text-neutral-400">{fmtDateTime(s.created_at)}</p>
              </div>
            ))}
            {!data.sends.length && <p className="text-xs text-neutral-400">No messages sent yet.</p>}
          </div>
        </div>

        <div className={sectionCls} data-testid="crm-360-notes">
          <p className={headCls}>Notes & tasks</p>
          <div className="flex gap-1 mb-2">
            <Input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note…" className="h-8 text-xs" data-testid="note-input" />
            <Button size="sm" variant="outline" onClick={addNote} data-testid="note-add-btn"><Plus size={13} /></Button>
          </div>
          <div className="space-y-1.5 max-h-32 overflow-y-auto mb-3">
            {data.notes.map((n) => (
              <p key={n.id} className="text-xs text-neutral-600" data-testid={`crm-note-${n.id}`}>{n.text} <span className="text-neutral-400">— {n.author}, {fmtDate(n.created_at)}</span></p>
            ))}
          </div>
          <div className="flex gap-1 mb-2">
            <Input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="Follow-up task…" className="h-8 text-xs" data-testid="task-input" />
            <Button size="sm" variant="outline" onClick={addTask} data-testid="task-add-btn"><Plus size={13} /></Button>
          </div>
          <div className="space-y-1">
            {data.tasks.map((t) => (
              <label key={t.id} className="flex items-center gap-2 text-xs" data-testid={`crm-task-${t.id}`}>
                <input type="checkbox" checked={t.done} onChange={(e) => api.patch(`/admin/crm/tasks/${t.id}`, { done: e.target.checked }).then(load)} />
                <span className={t.done ? "line-through text-neutral-400" : ""}>{t.title}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
